import { getApp, getApps, initializeApp, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  connectAuthEmulator,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  type Auth,
} from "firebase/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from "firebase/firestore";

const productionConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "AIzaSyBKkWFWeK31V4PMHrHv-LDisB9hcEV9hvk",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "logbookanaes.web.app",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "logbookanaes",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? "logbookanaes.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "766245898113",
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? "1:766245898113:web:312151a846f43d31e2ca80",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ?? "G-GXGLFJ30WJ",
};

export interface FirebaseServices {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
}

let singleton: FirebaseServices | undefined;
let emulatorConnected = false;

function buildAuth(app: FirebaseApp): Auth {
  try {
    return initializeAuth(app, {
      persistence: [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    return getAuth(app);
  }
}

function buildFirestore(app: FirebaseApp): Firestore {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch {
    return getFirestore(app);
  }
}

export function getFirebaseServices(): FirebaseServices {
  if (singleton) return singleton;
  const app = getApps().length > 0 ? getApp() : initializeApp(productionConfig);
  const auth = buildAuth(app);
  const firestore = buildFirestore(app);

  if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true" && !emulatorConnected) {
    connectAuthEmulator(
      auth,
      import.meta.env.VITE_FIREBASE_AUTH_EMULATOR ?? "http://127.0.0.1:9099",
      { disableWarnings: true },
    );
    connectFirestoreEmulator(
      firestore,
      import.meta.env.VITE_FIRESTORE_EMULATOR_HOST ?? "127.0.0.1",
      Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? "8080"),
    );
    emulatorConnected = true;
  }

  singleton = { app, auth, firestore };
  return singleton;
}
